/**
 * Global ErrorBoundary — wraps <App /> in src/index.js.
 *
 * React error boundaries can ONLY be class components; that's why this
 * file is a class. It catches render / lifecycle exceptions from any
 * descendant so a single crashing component (e.g. GoalDetail reading a
 * null field) no longer paints the whole app black.
 *
 * On error we log `error.message` and the React componentStack to the
 * browser console so ops can pinpoint the exact file/line users hit.
 * We deliberately DO NOT ship these strings to an external telemetry
 * endpoint here — keeps the fix scope minimal.
 */

import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Structured console output so it survives production minification —
    // `error.message` is preserved; the componentStack points at the
    // offending JSX (e.g. `in GoalDetail (at pages/GoalDetail.jsx:...)`).
    // eslint-disable-next-line no-console
    console.error(
      "[LEO ErrorBoundary]",
      (error && error.message) || String(error),
      "\ncomponentStack:",
      (info && info.componentStack) || "(no stack)",
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="global-error-boundary"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#0c0a09",
            color: "#e7e5e4",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: 420 }}>
            <div
              style={{
                fontFamily: "monospace",
                fontSize: 11,
                letterSpacing: "0.3em",
                color: "#f59e0b",
                textTransform: "uppercase",
                marginBottom: 16,
              }}
            >
              LEO
            </div>
            <p style={{ fontSize: 16, lineHeight: 1.55, marginBottom: 20 }}>
              Coś poszło nie tak, odśwież stronę.
              <br />
              <span style={{ color: "#a8a29e", fontSize: 14 }}>
                Something went wrong — please refresh the page.
              </span>
            </p>
            <button
              type="button"
              data-testid="global-error-refresh"
              onClick={() => window.location.reload()}
              style={{
                background: "#f59e0b",
                color: "#0c0a09",
                border: "none",
                borderRadius: 999,
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Odśwież / Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
